<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import ContactAPI from 'dashboard/api/contacts';

// Documentos da PESSOA (identidade, carteirinha do plano...). Ficam no
// contato, nao no negocio: todo negocio futuro dela enxerga, sem copiar o
// PDF a cada renovacao. A acao "Anexo" da etapa pode guardar aqui.
const props = defineProps({
  contactId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const documentos = ref([]);
const carregando = ref(false);
const enviando = ref(false);
const rotulo = ref('');
const arquivo = ref(null);

const carregar = async () => {
  if (!props.contactId) return;
  carregando.value = true;
  try {
    const { data } = await ContactAPI.documents(props.contactId);
    documentos.value = data?.payload || [];
  } catch {
    documentos.value = [];
  } finally {
    carregando.value = false;
  }
};

const escolher = event => {
  arquivo.value = event.target.files?.[0] || null;
};

const enviar = async () => {
  if (!arquivo.value) return;
  enviando.value = true;
  try {
    await ContactAPI.uploadDocument(
      props.contactId,
      arquivo.value,
      rotulo.value
    );
    toast.success(t('CONTACT_DOCUMENTS.UPLOADED'));
    rotulo.value = '';
    arquivo.value = null;
    await carregar();
  } catch {
    toast.error(t('CONTACT_DOCUMENTS.FAILED'));
  } finally {
    enviando.value = false;
  }
};

const remover = async doc => {
  try {
    await ContactAPI.destroyDocument(props.contactId, doc.id);
    toast.success(t('CONTACT_DOCUMENTS.DELETED'));
    await carregar();
  } catch {
    toast.error(t('CONTACT_DOCUMENTS.FAILED'));
  }
};

const tamanho = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const quando = iso =>
  iso
    ? new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : '';

watch(() => props.contactId, carregar);
onMounted(carregar);
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <span
        class="text-xs font-semibold uppercase tracking-wider text-n-slate-10"
      >
        {{ $t('CONTACT_DOCUMENTS.TITLE') }}
        <span v-if="documentos.length" class="ml-1 text-n-slate-9">
          {{ documentos.length }}
        </span>
      </span>
    </div>

    <div v-if="carregando" class="text-xs text-n-slate-10">
      {{ $t('CONTACT_DOCUMENTS.LOADING') }}
    </div>
    <p v-else-if="!documentos.length" class="text-xs text-n-slate-10 mb-2">
      {{ $t('CONTACT_DOCUMENTS.EMPTY') }}
    </p>
    <ul v-else class="mb-2 flex flex-col gap-1.5">
      <li
        v-for="doc in documentos"
        :key="doc.id"
        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5"
      >
        <span class="i-lucide-file-text size-4 shrink-0 text-n-slate-9" />
        <a
          :href="doc.url"
          target="_blank"
          rel="noopener noreferrer"
          class="min-w-0 flex-1 truncate text-xs font-medium text-n-slate-12 hover:underline"
          :title="doc.name"
        >
          {{ doc.label || doc.name }}
        </a>
        <span class="shrink-0 text-[10.5px] text-n-slate-9">
          {{ tamanho(doc.size) }} · {{ quando(doc.created_at) }}
          <template v-if="doc.deal_id">
            · {{ $t('CONTACT_DOCUMENTS.FROM_DEAL', { id: doc.deal_id }) }}
          </template>
        </span>
        <button
          class="shrink-0 text-n-slate-9 hover:text-n-ruby-10"
          :title="$t('CONTACT_DOCUMENTS.DELETE')"
          @click="remover(doc)"
        >
          <span class="i-lucide-trash-2 size-3.5" />
        </button>
      </li>
    </ul>

    <div class="flex flex-wrap items-center gap-1.5">
      <input
        v-model="rotulo"
        type="text"
        :placeholder="$t('CONTACT_DOCUMENTS.LABEL_PLACEHOLDER')"
        class="min-w-[140px] flex-1 rounded-lg border-n-weak bg-n-solid-1 py-1 text-xs"
      />
      <input
        type="file"
        class="max-w-[190px] text-[11px] text-n-slate-10"
        @change="escolher"
      />
      <button
        class="rounded-lg bg-n-brand px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
        :disabled="!arquivo || enviando"
        @click="enviar"
      >
        {{ $t('CONTACT_DOCUMENTS.UPLOAD') }}
      </button>
    </div>
  </div>
</template>
